/* eslint-disable no-nested-ternary */
import BouncingSpinner from 'components/BouncingSpinner';
import FileUpload from 'components/hoc/FileUpload';
import Document from 'components/Icons/Document';
import Label from 'components/inputs/Label';
import attach24 from 'images/icons/attach-24.svg';
import download from 'images/icons/download.svg';
import trash from 'images/icons/trash.svg';
import uploadIcon from 'images/icons/upload.svg';
import Immutable from 'immutable';
import React, { useState } from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { Doc } from 'records/Doc';
import { Submittal } from 'records/Submittal';
import { downloadSubmittalAttachment } from 'services/submittalService';
import { downloadFileWithAnchor, getDateString } from 'utils/general';
import { useTranslator } from '@bluebeam/react-i18n';
import withAppStore, { WithAppStoreProps } from 'utils/withAppStore';

interface Props extends WithAppStoreProps, RouteComponentProps {
  id: string;
  submittal: Submittal;
  files: Immutable.List<File>;
  attachButtonTitle: string;
  onRemoveClick: (
    evt: React.MouseEvent<HTMLButtonElement>
  ) => void | Promise<void>;
  onFileSelect: (files: FileList) => void;
}

const SubmittalAttachmentButtons = (props: Props) => {
  const { appState, submittal } = props;
  const [downloading, setDownloading] = useState<boolean>(false);
  const { translate } = useTranslator(['buttons']);

  const getSubmittalDocs = (): Immutable.List<Doc> => {
    const { documents } = appState;
    const attachmentSet = submittal.attachments.toSet();

    const submittalDocs = documents
      .filter((d) => attachmentSet.has(d.documentid))
      .valueSeq()
      .toList();

    return submittalDocs;
  };

  const downloadPDF = async (): Promise<void> => {
    try {
      setDownloading(true);
      const submittalDocs = getSubmittalDocs();
      const doc = submittalDocs.get(0);
      if (doc) {
        const url = await downloadSubmittalAttachment(
          doc.projectid,
          doc.documentid
        );

        const filename = `${doc.filename.replace(
          /\.[^/.]+$/,
          ''
        )}-${getDateString()}.pdf`;

        await downloadFileWithAnchor(url, filename);
      }
    } catch (err) {
      console.log('ViewerFooterDownload::downloadSheetPDF Error', err);
    } finally {
      setDownloading(false);
    }
  };

  const handleDownloadClick = async (): Promise<void> => {
    downloadPDF();
  };

  const { id, files } = props;

  const submittalDocs = getSubmittalDocs();
  const hasAttachment = files.count() !== 0 || submittalDocs.count() > 0;
  const firstAttachmentName = files.get(0)
    ? files.get(0)?.name
    : submittalDocs.get(0)?.filename;
  const showRemove = files.count() !== 0 && submittalDocs.count() === 0;

  return (
    <div id={id}>
      {hasAttachment ? (
        <div>
          <div>
            <Label label="Attachment" />
            <div
              className="row items-center pl3 font-size-5 bgd-white color-gray-7"
              style={{ height: '44px' }}
            >
              <div className="row mr1">
                <span className="wrem-1 hrem-1 flex-center">
                  <Document width="14px" height="18px" />
                </span>
              </div>
              <div>{firstAttachmentName || ''}</div>
            </div>
          </div>
          <div className="row mt3">
            {showRemove ? (
              <button
                id={`${id}-remove-btn`}
                className="btn-sm btn-link mr3 ml0"
                onClick={props.onRemoveClick}
              >
                <img className="mr1" src={trash} alt="trash icon" />
                {translate('buttons:remove')}
              </button>
            ) : downloading ? (
              <div className="flex-center ml4 ws6">
                <BouncingSpinner />
              </div>
            ) : (
              <button
                id={`${id}-download-btn`}
                className="btn-sm btn-link mr3 ml0"
                onClick={handleDownloadClick}
              >
                <img className="mr1" src={download} alt="trash icon" />
                {translate('buttons:download')}
              </button>
            )}

            <FileUpload
              id={`${id}-update-file-upload`}
              accept=".pdf"
              multiple={false}
              onFileSelect={(addFiles): void => props.onFileSelect(addFiles)}
            >
              {({ openFilePicker }): React.ReactNode => (
                <button
                  id={`${id}-file-upload-update-btn`}
                  className="btn-sm btn-link mr3"
                  onClick={openFilePicker}
                >
                  <img className="mr1" src={uploadIcon} alt="upload icon" />
                  {translate('buttons:update')}
                </button>
              )}
            </FileUpload>
          </div>
        </div>
      ) : (
        <div>
          <FileUpload
            id={`${id}-general-file-upload`}
            accept=".pdf"
            multiple={false}
            onFileSelect={(addFiles): void => props.onFileSelect(addFiles)}
          >
            {({ openFilePicker }): React.ReactNode => (
              <div>
                <button
                  id={`${id}-general-file-upload-btn`}
                  className="btn btn-outline-primary row items-center"
                  onClick={openFilePicker}
                >
                  <img className="mr1" src={attach24} alt="attach icon" />
                  {props.attachButtonTitle}
                </button>
              </div>
            )}
          </FileUpload>
        </div>
      )}
    </div>
  );
};

export default withRouter(withAppStore<Props>(SubmittalAttachmentButtons));
